// Chunked Pusher event helpers. The API splits any payload over 10KB into
// `${event}-chunked` events ({ id, index, chunk, final }) instead of letting
// Pusher drop it, so every bind needs a chunk-aware twin. Kept free of app
// imports so non-app bundles (wrappermanager.js) can use it too.

// a buffer missing chunks longer than this is abandoned (lost chunk)
const CHUNK_BUFFER_TTL = 60 * 1000

// Chunk failures should reach the exportable diagnostic log where available;
// src/helpers/pusher.js injects its createLogger at module init. Bundles
// without the app log (wrappermanager.js) keep the console default.
let logChunkError = (...args) => console.error(...args)
// returns the previous logger so callers can restore it
export const setChunkLogger = (fn) => {
	const previous = logChunkError
	logChunkError = fn
	return previous
}

const isComplete = ({ final, chunks }) => {
	// checks to make sure every expected chunk has been received — identity
	// check, since a zero-length chunk is valid but falsy
	if (!final) return false
	for (let i = 0; i < chunks.length; i++) {
		if (chunks[i] === undefined) return false
	}
	return true
}

export const collectChunks = (callback, ttlMs = CHUNK_BUFFER_TTL) => {
	// Buffers are keyed by chunk id only: each collectChunks() call returns a
	// fresh closure bound to a single (channel, event) binding, so payloads
	// from different channels can never share a buffer. (Mobile keys by
	// channel + id because it funnels all channels through one dispatcher.)
	const events = {}
	const evict = (id) => {
		clearTimeout(events[id]?.timer)
		delete events[id]
	}
	return ({ id, index, chunk, final }) => {
		if (!events[id]) {
			events[id] = { chunks: [], final: false }
		}
		const entry = events[id]
		// re-arm on every chunk: the TTL measures a gap in delivery, and this
		// timer is the only signal that a partial payload was abandoned —
		// without it a lost chunk is indistinguishable from "never sent" and
		// the buffer leaks for the page's lifetime
		clearTimeout(entry.timer)
		entry.timer = setTimeout(() => {
			logChunkError('pusher chunk buffer expired, payload dropped', id)
			evict(id)
		}, ttlMs)
		entry.chunks[index] = chunk
		entry.final = entry.final || final
		if (isComplete(entry)) {
			let data
			try {
				data = JSON.parse(entry.chunks.join(''))
			} catch (error) {
				logChunkError('pusher chunk error', error)
				return
			} finally {
				evict(id)
			}
			// outside the try: a throw from the app handler propagates like it
			// would on a plain-bound event instead of being mislabeled a chunk
			// error in the diagnostic log
			callback(data)
		}
	}
}

// Channel-scoped variants. Unlike bindEventWithChunking (which binds on the
// pusher instance and hears every channel), these bind on one channel so
// per-chat consumers don't receive other chats' events. They deliberately
// don't unbind: channel.unbind(event) would strip other consumers' handlers
// (ChatPanel + thread preview share bubble channels); cleanup is the caller's
// channel lifecycle (channel.unbind() on resubscribe clears -chunked too).
export const bindChannelEventWithChunking = (channel, event, handler) => {
	channel.bind(event, handler)
	channel.bind(`${event}-chunked`, collectChunks(handler))
}

const bindChannelEvents = (channel, prefix, eventsByName) => {
	Object.entries(eventsByName).forEach(([event, handler]) => {
		bindChannelEventWithChunking(
			channel,
			prefix ? `${prefix}\\${event}` : event,
			handler
		)
	})
}

export const bindChannelAppEvents = (channel, eventsByName) => {
	bindChannelEvents(channel, 'App\\Events', eventsByName)
}

// REST-style events use the bare event name (no App\Events\ prefix)
export const bindChannelRestEvents = (channel, eventsByName) => {
	bindChannelEvents(channel, '', eventsByName)
}

// Client events are size-capped by Pusher and never pass through the API's
// chunker, so no -chunked twin is bound
export const bindChannelClientEvents = (channel, eventsByName) => {
	Object.entries(eventsByName).forEach(([event, handler]) => {
		channel.bind(`client-App\\Events\\${event}`, handler)
	})
}

// Tracks blanket channel.unbind() calls so a consumer can tell whether the
// handlers it bound earlier are still attached — pusher can hand back the
// same channel object across a resubscribe that wiped them (see
// ChatPanel.connectPusher's alreadyBound guard). Only private-bubble
// channels are epoch-guarded; the sites that blanket-unbind one —
// doSubscribeToPrivateBubble (helpers/pusher.js), ChatPanel.unload, and the
// Meeting pusher module — must route through unbindChannel() below, or a
// consumer's guard goes permanently stale and its chat silently stops
// receiving events. Unbinds on user/push/presence/transcode channels don't
// need it (nothing epoch-guards those).
const bindEpochs = new WeakMap()

export const getBindEpoch = (channel) => bindEpochs.get(channel) || 0

export const bumpBindEpoch = (channel) => {
	if (channel) bindEpochs.set(channel, getBindEpoch(channel) + 1)
}

// Blanket-unbind a channel AND bump its epoch as one operation. Bumps even
// when unbind throws partway: handlers may be partially removed, and a stale
// epoch would make a consumer's alreadyBound guard skip rebinding a channel
// whose handlers are gone — a silently quiet chat.
export const unbindChannel = (channel) => {
	try {
		channel?.unbind?.()
	} catch (error) {
		// through the injected logger so it reaches the exportable
		// diagnostic log — handlers may be half-removed after this
		logChunkError('channel unbind failed', error)
	}
	bumpBindEpoch(channel)
}
